import type {
  CachedWorkspace,
  CloudCacheStore,
} from "./cloud-repository";
import type { CloudMutation } from "./state-codec";

const DATABASE_VERSION = 1;
const WORKSPACE_STORE = "workspaces";
const OUTBOX_STORE = "outbox";
const OUTBOX_UID_INDEX = "by_uid";

type OutboxRow = Readonly<{
  uid: string;
  mutationId: string;
  mutation: CloudMutation;
}>;

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener(
      "error",
      () => reject(request.error ?? new Error("IndexedDB request failed")),
      { once: true },
    );
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true });
    transaction.addEventListener(
      "abort",
      () => reject(transaction.error ?? new Error("IndexedDB transaction aborted")),
      { once: true },
    );
    transaction.addEventListener(
      "error",
      () => reject(transaction.error ?? new Error("IndexedDB transaction failed")),
      { once: true },
    );
  });
}

function deleteUidRows(
  index: IDBIndex,
  uid: string,
  keyRange: typeof IDBKeyRange,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = index.openCursor(keyRange.only(uid));
    request.addEventListener("error", () => reject(request.error), { once: true });
    request.addEventListener("success", () => {
      const cursor = request.result;
      if (cursor === null) {
        resolve();
        return;
      }
      cursor.delete();
      cursor.continue();
    });
  });
}

export class IndexedDbCloudCache implements CloudCacheStore {
  private databasePromise: Promise<IDBDatabase> | null = null;

  constructor(
    private readonly databaseName = "second-brain-cloud-cache-v1",
    private readonly factory: IDBFactory = indexedDB,
    private readonly keyRange: typeof IDBKeyRange = IDBKeyRange,
  ) {}

  private open(): Promise<IDBDatabase> {
    if (this.databasePromise !== null) return this.databasePromise;
    this.databasePromise = new Promise((resolve, reject) => {
      const request = this.factory.open(this.databaseName, DATABASE_VERSION);
      request.addEventListener("upgradeneeded", () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(WORKSPACE_STORE)) {
          database.createObjectStore(WORKSPACE_STORE, { keyPath: "uid" });
        }
        if (!database.objectStoreNames.contains(OUTBOX_STORE)) {
          const outbox = database.createObjectStore(OUTBOX_STORE, {
            keyPath: ["uid", "mutationId"],
          });
          outbox.createIndex(OUTBOX_UID_INDEX, "uid", { unique: false });
        }
      });
      request.addEventListener("success", () => resolve(request.result), { once: true });
      request.addEventListener(
        "error",
        () => reject(request.error ?? new Error("Cloud cache could not be opened")),
        { once: true },
      );
      request.addEventListener(
        "blocked",
        () => reject(new Error("Cloud cache upgrade is blocked by another tab")),
        { once: true },
      );
    });
    return this.databasePromise;
  }

  async loadWorkspace(uid: string): Promise<CachedWorkspace | null> {
    const database = await this.open();
    const transaction = database.transaction(WORKSPACE_STORE, "readonly");
    const result = await requestResult(
      transaction.objectStore(WORKSPACE_STORE).get(uid) as IDBRequest<
        CachedWorkspace | undefined
      >,
    );
    await transactionDone(transaction);
    return result ?? null;
  }

  async saveWorkspace(workspace: CachedWorkspace): Promise<void> {
    const database = await this.open();
    const transaction = database.transaction(WORKSPACE_STORE, "readwrite");
    transaction.objectStore(WORKSPACE_STORE).put(workspace);
    await transactionDone(transaction);
  }

  async saveStateAndMutations(
    workspace: CachedWorkspace,
    mutations: readonly CloudMutation[],
  ): Promise<void> {
    const database = await this.open();
    const transaction = database.transaction(
      [WORKSPACE_STORE, OUTBOX_STORE],
      "readwrite",
    );
    transaction.objectStore(WORKSPACE_STORE).put(workspace);
    const outbox = transaction.objectStore(OUTBOX_STORE);
    await deleteUidRows(outbox.index(OUTBOX_UID_INDEX), workspace.uid, this.keyRange);
    for (const mutation of mutations) {
      const row: OutboxRow = {
        uid: workspace.uid,
        mutationId: mutation.mutationId,
        mutation,
      };
      outbox.put(row);
    }
    await transactionDone(transaction);
  }

  async listOutbox(uid: string): Promise<CloudMutation[]> {
    const database = await this.open();
    const transaction = database.transaction(OUTBOX_STORE, "readonly");
    const rows = await requestResult(
      transaction
        .objectStore(OUTBOX_STORE)
        .index(OUTBOX_UID_INDEX)
        .getAll(uid) as IDBRequest<OutboxRow[]>,
    );
    await transactionDone(transaction);
    return rows.map((row) => row.mutation);
  }

  async deleteOutbox(uid: string, mutationIds: readonly string[]): Promise<void> {
    if (mutationIds.length === 0) return;
    const database = await this.open();
    const transaction = database.transaction(OUTBOX_STORE, "readwrite");
    const outbox = transaction.objectStore(OUTBOX_STORE);
    for (const mutationId of mutationIds) outbox.delete([uid, mutationId]);
    await transactionDone(transaction);
  }

  async clearUid(uid: string): Promise<void> {
    const database = await this.open();
    const transaction = database.transaction(
      [WORKSPACE_STORE, OUTBOX_STORE],
      "readwrite",
    );
    transaction.objectStore(WORKSPACE_STORE).delete(uid);
    await deleteUidRows(
      transaction.objectStore(OUTBOX_STORE).index(OUTBOX_UID_INDEX),
      uid,
      this.keyRange,
    );
    await transactionDone(transaction);
  }
}
